import { useCallback, useEffect, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { getNavigation, listActions } from '@plone/volto/actions';
import {
  getBaseUrl,
  hasApiExpander,
  flattenToAppURL,
} from '@plone/volto/helpers';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';
import config from '@plone/registry';
import { useRestaurantSettings } from '../../../hooks/useRestaurantSettings';
import { useSiteLogo } from '../../../hooks/useSiteLogo';
import { formatTelHref } from '../../../helpers/phone';
import './Header.scss';

const Header = ({ pathname }: { pathname: string }) => {
  const settings = useRestaurantSettings();
  const dispatch = useDispatch();
  const [menuOpen, setMenuOpen] = useState(false);

  const token = useSelector(
    (state: any) => state.userSession.token,
    shallowEqual,
  );
  const navItems = useSelector(
    (state: any) => state.navigation?.items || [],
    shallowEqual,
  );
  const siteActions = useSelector(
    (state: any) => state.actions?.actions?.portal_tabs || [],
    shallowEqual,
  );
  const siteLogo = useSiteLogo();

  useEffect(() => {
    if (!hasApiExpander('navigation', getBaseUrl(pathname))) {
      dispatch(
        getNavigation(getBaseUrl(pathname), config.settings.navDepth) as any,
      );
    }
    if (!hasApiExpander('actions', getBaseUrl(pathname))) {
      dispatch(listActions(getBaseUrl(pathname)) as any);
    }
  }, [pathname, token, dispatch]);

  // Close drawer on route change
  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  // Close on Escape; lock body scroll while open
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [menuOpen]);

  const closeMenu = useCallback(() => setMenuOpen(false), []);

  const nousTrouverAction = siteActions.find(
    (action: any) => action.id === 'find_us',
  );
  // The single booking entry point of the header: it leads to the Zenchef
  // module of the home page.
  const bookAction = siteActions.find((action: any) => action.id === 'book');
  const actionUrls = [nousTrouverAction, bookAction]
    .filter(Boolean)
    .map((action: any) => flattenToAppURL(action.url));

  const contentItems = navItems.filter(
    (item: any) =>
      item.url !== '' &&
      item.url !== '/' &&
      !actionUrls.includes(flattenToAppURL(item.url)),
  );

  const links = (
    <>
      <NavLink exact to="/" activeClassName="active" onClick={closeMenu}>
        Accueil
      </NavLink>
      {contentItems.map((item: any) => (
        <NavLink
          key={item.url}
          to={item.url}
          activeClassName="active"
          onClick={closeMenu}
        >
          {item.title}
        </NavLink>
      ))}
      {nousTrouverAction && (
        <UniversalLink href={nousTrouverAction.url} onClick={closeMenu}>
          {nousTrouverAction.title}
        </UniversalLink>
      )}
      {bookAction && (
        <UniversalLink
          href={bookAction.url}
          className="nav-book"
          onClick={closeMenu}
        >
          {bookAction.title}
        </UniversalLink>
      )}
      {settings.phone_primary && (
        <a
          className="nav-phone"
          href={formatTelHref(settings.phone_primary)}
          aria-label={`Téléphone : ${settings.phone_primary}`}
          onClick={closeMenu}
        >
          {settings.phone_primary}
        </a>
      )}
    </>
  );

  return (
    <>
      <div className="nav" role="banner">
        <div className="shell nav-inner">
          <Link className="brand" to="/" aria-label="À Ciel Ouvert — accueil">
            {siteLogo ? (
              <img className="brand-logo" src={siteLogo} alt="À Ciel Ouvert" />
            ) : (
              <span className="brand-name">À ciel ouvert</span>
            )}
            <span className="brand-sub">{settings.city}</span>
          </Link>
          <nav className="nav-links" aria-label="Navigation principale">
            {links}
          </nav>
          <button
            type="button"
            className="nav-burger"
            aria-label={menuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
            aria-expanded={menuOpen}
            aria-controls="mobile-nav"
            onClick={() => setMenuOpen((v) => !v)}
          >
            <span className={`nav-burger-icon${menuOpen ? ' is-open' : ''}`} />
          </button>
        </div>
      </div>
      <div
        id="mobile-nav"
        className={`nav-drawer${menuOpen ? ' is-open' : ''}`}
        aria-hidden={!menuOpen}
      >
        <nav className="nav-drawer-links" aria-label="Navigation mobile">
          {links}
        </nav>
      </div>
      {menuOpen && (
        <button
          type="button"
          className="nav-drawer-backdrop"
          aria-label="Fermer le menu"
          onClick={closeMenu}
        />
      )}
    </>
  );
};

export default Header;
